import React, { useState } from 'react';
import { Button, Card, Upload, useToast, Modal } from '../ui';
import { CloudArrowUpIcon, ExclamationTriangleIcon, CheckCircleIcon } from '@heroicons/react/24/outline';
import { supabase } from '../config/supabase';

interface CsvRow {
  brand: string;
  description: string;
  category: string;
  color: string;
  size: string;
  rentalPrice: number;
}

interface ImportStats {
  totalDresses: number;
  brandsCreated: number;
  categoriesCreated: number;
}

const CsvImportEdgeFunction: React.FC = () => {
  const [csvData, setCsvData] = useState<CsvRow[]>([]);
  const [importModalVisible, setImportModalVisible] = useState(false);
  const [isImporting, setIsImporting] = useState(false);
  const [importStats, setImportStats] = useState<ImportStats | null>(null);
  const [importError, setImportError] = useState<string | null>(null);
  
  const { success, error: showError } = useToast();

  const parseCsvFile = (file: File): Promise<CsvRow[]> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        try {
          const text = e.target?.result as string;
          const lines = text.split('\n').filter(line => line.trim());
          
          if (lines.length < 2) {
            reject(new Error('CSV file must have at least a header and one data row'));
            return;
          }

          // Skip header row and parse data
          const dataRows = lines.slice(1).map((line, index) => {
            const columns = line.split(',').map(col => col.trim());
            
            if (columns.length < 6) {
              throw new Error(`Row ${index + 2}: Invalid number of columns (expected 6, got ${columns.length})`);
            }

            const rentalPrice = parseFloat(columns[5]) || 0;
            
            return {
              brand: columns[0] || '',
              description: columns[1] || '',
              category: columns[2] || '',
              color: columns[3] || '',
              size: columns[4] || '',
              rentalPrice: rentalPrice
            };
          }).filter(row => row.brand && row.description); // Filter out empty rows

          resolve(dataRows);
        } catch (error) {
          reject(error);
        }
      };
      reader.onerror = () => reject(new Error('Failed to read file'));
      reader.readAsText(file);
    });
  };

  const handleFileUpload = async (file: File) => {
    try {
      const data = await parseCsvFile(file);
      setCsvData(data);
      setImportModalVisible(true);
      setImportStats(null);
      setImportError(null);
      success(`Parsed ${data.length} rows from CSV file`);
    } catch (error: any) {
      showError(`Error parsing CSV: ${error.message}`);
    }
  };

  const handleImport = async () => {
    if (csvData.length === 0) return;

    setIsImporting(true);
    setImportError(null);

    try {
      // Call the Edge Function
      const { data, error } = await supabase.functions.invoke('bulk-import-dresses', {
        body: { csvData }
      });

      if (error) {
        throw new Error(error.message || 'Failed to invoke import function');
      }

      if (!data.success) {
        throw new Error(data.error || 'Import failed');
      }

      setImportStats(data.stats);
      success(data.message);

    } catch (error: any) {
      console.error('Import error:', error);
      setImportError(error.message);
      showError(`Import failed: ${error.message}`);
    } finally {
      setIsImporting(false);
    }
  };

  const handleModalClose = () => {
    setImportModalVisible(false);
    setCsvData([]);
    setImportStats(null);
    setImportError(null);
  };

  return (
    <Card>
      <div className="p-6">
        <h2 className="text-xl font-semibold mb-4 flex items-center">
          <CloudArrowUpIcon className="h-6 w-6 mr-2" />
          CSV Import (Edge Function)
        </h2>
        
        <div className="space-y-4">
          <div className="text-sm text-gray-600">
            <p>Upload a CSV file for server-side bulk processing:</p>
            <p className="font-mono text-xs mt-1">Brand, Description, Category, Color, Size, Rental Price</p>
          </div>

          <Upload
            accept=".csv"
            beforeUpload={async (file: File) => {
              await handleFileUpload(file);
              return false; // Prevent default upload behavior
            }}
          >
            <div className="border-2 border-dashed border-gray-300 rounded-lg p-8 text-center hover:border-gray-400 transition-colors">
              <CloudArrowUpIcon className="h-12 w-12 text-gray-400 mx-auto mb-4" />
              <p className="text-lg font-medium text-gray-900">Upload CSV File</p>
            </div>
          </Upload>
        </div>
      </div>

      {/* Import Modal */}
      <Modal
        title="Bulk CSV Import"
        isOpen={importModalVisible}
        onClose={handleModalClose}
        size="xl"
        footer={
          <div className="flex space-x-2">
            <Button variant="outline" onClick={handleModalClose}>
              Cancel
            </Button>
            <Button
              variant="primary"
              loading={isImporting}
              onClick={handleImport}
              disabled={csvData.length === 0}
            >
              {isImporting ? 'Processing...' : `Import ${csvData.length} Dresses`}
            </Button>
          </div>
        }
      >
        <div className="space-y-4">
          {!importStats && !importError && (
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <div className="flex items-start">
                <ExclamationTriangleIcon className="h-5 w-5 text-blue-600 mr-2 mt-0.5" />
                <div className="text-sm">
                  <p className="font-medium text-blue-800">Edge Function Import will:</p>
                  <ul className="mt-2 text-blue-700 list-disc list-inside">
                    <li>Process in batches of 100 records</li>
                    <li>Create missing brands and categories automatically</li>
                    <li>Generate unique EAN-13 barcodes for each dress</li>
                    <li>Handle large files without browser timeouts</li>
                  </ul>
                </div>
              </div>
            </div>
          )}

          {importStats && (
            <div className="bg-green-50 border border-green-200 rounded-lg p-4">
              <div className="flex items-center">
                <CheckCircleIcon className="h-5 w-5 text-green-600 mr-2" />
                <h3 className="font-medium text-green-800">Import Successful!</h3>
              </div>
              <div className="mt-2 text-sm text-green-700">
                <p>✅ Total dresses imported: <strong>{importStats.totalDresses}</strong></p>
                <p>✅ Brands created: <strong>{importStats.brandsCreated}</strong></p>
                <p>✅ Categories created: <strong>{importStats.categoriesCreated}</strong></p>
              </div>
            </div>
          )}

          {importError && (
            <div className="bg-red-50 border border-red-200 rounded-lg p-4">
              <h4 className="font-medium text-red-800 mb-2">Import Error:</h4>
              <p className="text-sm text-red-700 font-mono">{importError}</p>
            </div>
          )}

          {csvData.length > 0 && !importStats && (
            <div className="text-center text-gray-600">
              <p>Ready to import <strong>{csvData.length}</strong> dress records</p>
            </div>
          )}
        </div>
      </Modal>
    </Card>
  );
};

export default CsvImportEdgeFunction; 